import React, { useState, useEffect, useMemo } from 'react';
import { profilePictureService } from '../../utils/profilePictureService';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import Image from '../AppImage';
import Icon from '../AppIcon';
import Textarea from '../ui/Textarea';
import Toast from '../ui/Toast';

const ProfilePictureReviews = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'history', 'stats'
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all', 'approved', 'rejected'
  const [historySearch, setHistorySearch] = useState('');
  const [historyDateRange, setHistoryDateRange] = useState({ from: '', to: '' });
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'user'
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPagePending, setCurrentPagePending] = useState(1);
  const itemsPerPage = 12;
  const [enlargedImage, setEnlargedImage] = useState(null); // { url, title }
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchPendingReviews();
    fetchReviewHistory();
  }, []);

  const fetchReviewHistory = async () => {
    try {
      const result = await profilePictureService.getReviewHistory();
      if (result.success) {
        setHistory(result.data);
      } else {
        console.error('Error fetching history:', result.error);
        setToast({
          show: true,
          message: 'Failed to load review history',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      setToast({
        show: true,
        message: 'Failed to load review history',
        type: 'error'
      });
    }
  };

  const fetchPendingReviews = async () => {
    setLoading(true);
    try {
      const result = await profilePictureService.getPendingReviews();
      if (result.success) {
        setReviews(result.data);
        setCurrentPagePending(1); // Reset to page 1
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchPendingReviews();
      await fetchReviewHistory();
      setToast({
        show: true,
        message: 'Reviews refreshed successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error refreshing:', error);
      setToast({
        show: true,
        message: 'Failed to refresh reviews',
        type: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleApprove = async (reviewId) => {
    setProcessingId(reviewId);
    try {
      const result = await profilePictureService.approveProfilePicture(reviewId, user.id);

      if (result.success) {
        // Show success toast
        setToast({
          show: true,
          message: 'Profile picture approved successfully! User will be notified.',
          type: 'success'
        });
        
        // Refresh reviews and history
        await fetchPendingReviews();
        await fetchReviewHistory();
        setSelectedReview(null);
      } else {
        setToast({
          show: true,
          message: result.error || 'Failed to approve profile picture',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('[ProfilePictureReviews] Error: Approve failed');
      setToast({
        show: true,
        message: 'Failed to approve profile picture',
        type: 'error'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (reviewId) => {
    if (!rejectionReason.trim()) {
      setToast({
        show: true,
        message: 'Please provide a rejection reason',
        type: 'warning'
      });
      return;
    }
    setProcessingId(reviewId);
    try {
      const result = await profilePictureService.rejectProfilePicture(
        reviewId, 
        user.id, 
        rejectionReason
      );

      if (result.success) {
        // Show success toast
        setToast({
          show: true,
          message: 'Profile picture rejected! User will be notified with the reason.',
          type: 'success'
        });
        
        // Refresh reviews
        await fetchPendingReviews();
        await fetchReviewHistory();
        setSelectedReview(null);
        setRejectionReason('');
      } else {
        setToast({
          show: true,
          message: result.error || 'Failed to reject profile picture',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('[ProfilePictureReviews] Error: Reject failed');
      setToast({
        show: true,
        message: 'Failed to reject profile picture',
        type: 'error'
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalReviews = reviews.length + history.length;
    const totalApproved = history.filter(h => h.status === 'approved').length;
    const totalRejected = history.filter(h => h.status === 'rejected').length;
    const approvalRate = totalReviews > 0 ? Math.round((totalApproved / totalReviews) * 100) : 0;
    const rejectionRate = totalReviews > 0 ? Math.round((totalRejected / totalReviews) * 100) : 0;

    // Get rejection reasons frequency
    const rejectionReasons = {};
    history
      .filter(h => h.status === 'rejected')
      .forEach(h => {
        const reason = h.rejection_reason || 'No reason provided';
        rejectionReasons[reason] = (rejectionReasons[reason] || 0) + 1;
      });

    // Get reviews by date
    const reviewsByDate = {};
    history.forEach(h => {
      const date = new Date(h.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      reviewsByDate[date] = (reviewsByDate[date] || 0) + 1;
    });

    return {
      totalReviews,
      totalPending: reviews.length,
      totalApproved,
      totalRejected,
      approvalRate,
      rejectionRate,
      rejectionReasons,
      reviewsByDate
    };
  }, [reviews, history]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [historyFilter, historySearch, historyDateRange.from, historyDateRange.to, sortBy]);

  // Filter and sort history
  const filteredHistory = useMemo(() => {
    let filtered = history;

    // Apply status filter
    if (historyFilter !== 'all') {
      filtered = filtered.filter(h => h.status === historyFilter);
    }

    // Apply search filter
    if (historySearch.trim()) {
      const searchTerm = historySearch.toLowerCase();
      filtered = filtered.filter(h => 
        h.user?.full_name?.toLowerCase().includes(searchTerm) ||
        h.user?.email?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply date range filter
    if (historyDateRange.from) {
      filtered = filtered.filter(h => new Date(h.created_at) >= new Date(historyDateRange.from));
    }
    if (historyDateRange.to) {
      filtered = filtered.filter(h => new Date(h.created_at) <= new Date(historyDateRange.to));
    }

    // Sort
    const sorted = [...filtered];
    if (sortBy === 'newest') {
      sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'oldest') {
      sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortBy === 'user') {
      sorted.sort((a, b) => a.user?.full_name?.localeCompare(b.user?.full_name || ''));
    }

    return sorted;
  }, [history, historyFilter, historySearch, historyDateRange, sortBy]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHistory = filteredHistory.slice(startIndex, endIndex);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Profile Picture Reviews</h2>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isRefreshing}
          iconName={isRefreshing ? "Loader" : "RotateCw"}
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'pending'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon name="CheckCircle" size={14} className="inline mr-2" />
            Pending ({reviews.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'history'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon name="Archive" size={14} className="inline mr-2" />
            History
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'stats'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon name="BarChart3" size={14} className="inline mr-2" />
            Statistics
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="pt-4">
        {/* Pending Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-6">
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="CheckCircle" size={48} className="text-text-tertiary mx-auto mb-4" />
                <p className="text-text-secondary">No pending reviews</p>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {reviews.slice((currentPagePending - 1) * itemsPerPage, currentPagePending * itemsPerPage).map((review) => (
          <div 
            key={review.id} 
            className="bg-background-secondary rounded-lg p-6 border border-border shadow-sm hover:shadow-md transition-shadow"
          >
            {/* User Info Header */}
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-background flex-shrink-0">
                {review.user.profile_picture ? (
                  <Image
                    src={review.user.profile_picture}
                    alt="Current"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="User" size={24} className="text-text-tertiary" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{review.user.full_name}</h3>
                <p className="text-sm text-text-secondary truncate">{review.user.email}</p>
              </div>
            </div>

            {/* New Photo Preview */}
            <div className="mb-4">
              <p className="text-sm font-medium text-text-secondary mb-2">New Photo:</p>
              <div 
                className="w-full aspect-square rounded-lg overflow-hidden bg-background border border-border cursor-pointer hover:opacity-75 transition-opacity"
                onClick={() => setEnlargedImage({ url: review.new_picture, title: 'New Photo' })}
              >
                <Image
                  src={review.new_picture}
                  alt="New profile picture"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Comparison - Previous Photo */}
            {review.previous_picture && (
              <div className="mb-4">
                <p className="text-sm font-medium text-text-secondary mb-2">Previous Photo:</p>
                <div 
                  className="w-24 h-24 rounded-lg overflow-hidden bg-background border border-border cursor-pointer hover:opacity-75 transition-opacity"
                  onClick={() => setEnlargedImage({ url: review.previous_picture, title: 'Previous Photo' })}
                >
                  <Image
                    src={review.previous_picture}
                    alt="Previous profile picture"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Submission Date */}
            <div className="text-xs text-text-tertiary mb-4">
              <Icon name="Clock" size={12} className="inline mr-1" />
              Submitted: {new Date(review.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                className="flex-1"
                onClick={() => handleApprove(review.id)}
                disabled={processingId === review.id}
                iconName={processingId === review.id ? "Loader" : "Check"}
              >
                {processingId === review.id ? 'Approving...' : 'Approve'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => setSelectedReview(review)}
                disabled={processingId === review.id}
                iconName="X"
              >
                Reject
              </Button>
            </div>
          </div>
        ))}
                </div>

                {/* Pagination Controls for Pending */}
                {Math.ceil(reviews.length / itemsPerPage) > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-6 border-t border-border">
                    <div className="flex items-center justify-center gap-4">
                      <p className="text-sm text-text-secondary">
                        Showing {((currentPagePending - 1) * itemsPerPage) + 1}-{Math.min(currentPagePending * itemsPerPage, reviews.length)} of {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPagePending(prev => Math.max(1, prev - 1))}
                      disabled={currentPagePending === 1}
                      iconName="ChevronLeft"
                    >
                      Previous
                    </Button>
                    
                    <div className="flex gap-1">
                      {Array.from({ length: Math.ceil(reviews.length / itemsPerPage) }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPagePending(page)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            currentPagePending === page
                              ? 'bg-primary text-white'
                              : 'bg-background-secondary text-text-primary hover:bg-background-tertiary'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPagePending(prev => Math.min(Math.ceil(reviews.length / itemsPerPage), prev + 1))}
                      disabled={currentPagePending === Math.ceil(reviews.length / itemsPerPage)}
                      iconName="ChevronRight"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Moderation History</h2>
            </div>

            {/* Filters */}
            <div className="bg-background-secondary rounded-lg p-4 border border-border space-y-4">
              <div className="grid md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1 text-text-primary">Status</label>
                  <select
                    value={historyFilter}
                    onChange={(e) => setHistoryFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                  >
                    <option value="all">All Statuses</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-text-primary">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="user">User Name</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-text-primary">From Date</label>
                  <input
                    type="date"
                    value={historyDateRange.from}
                    onChange={(e) => setHistoryDateRange({ ...historyDateRange, from: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-text-primary">To Date</label>
                  <input
                    type="date"
                    value={historyDateRange.to}
                    onChange={(e) => setHistoryDateRange({ ...historyDateRange, to: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text-primary">Search</label>
                <input
                  type="text"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
            </div>

            {/* History Results */}
            {filteredHistory.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Archive" size={48} className="text-text-tertiary mx-auto mb-4" />
                <p className="text-text-secondary">No reviews found</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-text-secondary">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredHistory.length)} of {filteredHistory.length} result{filteredHistory.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-text-tertiary">
                    Page {currentPage} of {totalPages}
                  </p>
                </div>
                {paginatedHistory.map((item) => (
                  <div 
                    key={item.id} 
                    className="bg-background-secondary rounded-lg p-4 border border-border hover:border-primary transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-background flex-shrink-0">
                          {item.user?.profile_picture ? (
                            <Image
                              src={item.user.profile_picture}
                              alt={item.user.full_name}
                              className="w-full h-full object-cover cursor-pointer hover:opacity-75 transition-opacity"
                              onClick={() => setEnlargedImage({ url: item.user.profile_picture, title: `${item.user.full_name}'s Photo` })}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Icon name="User" size={20} className="text-text-tertiary" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold truncate">{item.user?.full_name}</h4>
                          <p className="text-sm text-text-secondary truncate">{item.user?.email}</p>
                          {item.status === 'rejected' && item.rejection_reason && (
                            <p className="text-sm text-red-600 mt-1 italic">"{item.rejection_reason}"</p>
                          )}
                          <p className="text-xs text-text-tertiary mt-1">
                            {new Date(item.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.status === 'approved' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {item.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      iconName="ChevronLeft"
                    >
                      Previous
                    </Button>
                    
                    <div className="flex gap-1">
                      {getPageNumbers().map((page, idx) => (
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-3 py-2 text-text-tertiary">
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === page
                                ? 'bg-primary text-white'
                                : 'bg-background-secondary text-text-primary hover:bg-background-tertiary'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      iconName="ChevronRight"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Moderation Statistics</h2>

            {/* Key Metrics */}
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard
                icon="CheckCircle"
                title="Pending Reviews"
                value={statistics.totalPending}
                color="blue"
              />
              <StatCard
                icon="Check"
                title="Approved"
                value={statistics.totalApproved}
                color="green"
              />
              <StatCard
                icon="X"
                title="Rejected"
                value={statistics.totalRejected}
                color="red"
              />
              <StatCard
                icon="TrendingUp"
                title="Approval Rate"
                value={`${statistics.approvalRate}%`}
                color="emerald"
              />
              <StatCard
                icon="Eye"
                title="Total Reviewed"
                value={statistics.totalReviews}
                color="purple"
              />
            </div>

            {/* Rejection Reasons */}
            {Object.keys(statistics.rejectionReasons).length > 0 && (
              <div className="bg-background-secondary rounded-lg p-6 border border-border">
                <h3 className="text-lg font-semibold mb-4">Top Rejection Reasons</h3>
                <div className="space-y-3">
                  {Object.entries(statistics.rejectionReasons)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 5)
                    .map(([reason, count]) => (
                      <div key={reason} className="flex items-center justify-between">
                        <span className="text-text-secondary">{reason}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-background rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-red-500" 
                              style={{ width: `${(count / statistics.totalRejected) * 100}%` }}
                            />
                          </div>
                          <span className="font-semibold w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Reviews Timeline */}
            {Object.keys(statistics.reviewsByDate).length > 0 && (
              <div className="bg-background-secondary rounded-lg p-6 border border-border">
                <h3 className="text-lg font-semibold mb-4">Reviews by Date</h3>
                <div className="space-y-2">
                  {Object.entries(statistics.reviewsByDate)
                    .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                    .slice(0, 7)
                    .map(([date, count]) => (
                      <div key={date} className="flex items-center justify-between text-sm">
                        <span className="text-text-secondary">{date}</span>
                        <span className="font-semibold">{count} review{count !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEnlargedImage(null);
            }
          }}
        >
          <div className="bg-background rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto relative">
            {/* Close button */}
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute top-4 right-4 p-2 bg-background-secondary rounded-full hover:bg-background-tertiary transition-colors z-10 border border-border"
            >
              <Icon name="X" size={24} className="text-text-primary" />
            </button>
            
            {/* Image Title */}
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold">{enlargedImage.title}</h3>
            </div>
            
            {/* Enlarged Image */}
            <div className="flex items-center justify-center bg-background">
              <Image
                src={enlargedImage.url}
                alt={enlargedImage.title}
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {selectedReview && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget && processingId !== selectedReview.id) {
              setSelectedReview(null);
              setRejectionReason('');
            }
          }}
        >
          <div className="bg-background rounded-lg p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="AlertCircle" size={24} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold">Reject Profile Picture</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-text-secondary mb-2">
                You are about to reject <strong>{selectedReview.user.full_name}'s</strong> profile picture.
              </p>
              <p className="text-sm text-text-tertiary mb-4">
                Please provide a clear reason for the rejection:
              </p>
              
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Image contains inappropriate content, poor quality, not a profile photo, etc."
                rows={4}
                className="mb-2"
              />
              <p className="text-xs text-text-tertiary">
                This reason will be visible to the user.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedReview(null);
                  setRejectionReason('');
                }}
                className="flex-1"
                disabled={processingId === selectedReview.id}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleReject(selectedReview.id)}
                className="flex-1"
                disabled={processingId === selectedReview.id || !rejectionReason.trim()}
                iconName={processingId === selectedReview.id ? "Loader" : "X"}
              >
                {processingId === selectedReview.id ? 'Rejecting...' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
          autoClose={3000}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, title, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200'
  };

  return (
    <div className={`rounded-lg p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon name={icon} size={24} />
      </div>
      <p className="text-sm font-medium text-text-secondary mb-1">{title}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

export default ProfilePictureReviews;
